import { formatIsoDateFr } from "@/lib/organisation-day"

/**
 * Mise en forme des dates de session pour l'apprenant.
 *
 * Une session peut n'avoir aucune date : le backend expose `start_date` et
 * `end_date` en `Option<NaiveDate>`, donc l'API renvoie `null` tant que
 * l'organisme n'a pas calé le calendrier. Or `new Date(null)` vaut le
 * 1er janvier 1970 — la page afficherait donc une date fausse et plausible
 * plutôt qu'une absence. C'est pire qu'une erreur : personne ne la signale.
 *
 * D'où le rendu explicite « Dates à confirmer », et une seule implémentation
 * partagée par les trois écrans du portail qui la dupliquaient chacun à leur
 * façon.
 */

/** Ce que les écrans montrent quand le calendrier n'est pas encore posé. */
export const DATE_A_CONFIRMER = "Dates à confirmer"

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Une date de session s'affiche telle qu'écrite.
 * Un instant suit le fuseau du navigateur.
 * Le portail ne reçoit pas celui de l'organisme.
 */
function format(date: string, options: Intl.DateTimeFormatOptions): string {
  if (CALENDAR_DAY.test(date)) return formatIsoDateFr(date, options)
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return DATE_A_CONFIRMER
  return parsed.toLocaleDateString("fr-FR", options)
}

/** « lundi 3 mars 2026 » — en-tête de fiche, où la date est l'information. */
export function formatLongDate(date: string | null): string {
  if (date === null) return DATE_A_CONFIRMER
  return format(date, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

/** « 3 mars 2026 » — listes et cartes, où la date accompagne le reste. */
export function formatShortDate(date: string | null): string {
  if (date === null) return DATE_A_CONFIRMER
  return format(date, { day: "numeric", month: "long", year: "numeric" })
}

/**
 * « Du 3 mars 2026 au 5 mars 2026 », ou l'absence quand une borne manque.
 *
 * Une plage à une seule borne ne se dit pas : « Du 3 mars au 1 janvier 1970 »
 * est exactement ce que cette fonction existe pour empêcher.
 */
export function formatDateRange(start: string | null, end: string | null): string {
  if (start === null || end === null) return DATE_A_CONFIRMER
  return `Du ${formatShortDate(start)} au ${formatShortDate(end)}`
}
